import React from "react";
import moment from "moment";
import { Icon, Tooltip } from "antd";
import { OrderedMap } from "immutable";
import { formatCurrency } from "utilities/currency";
import { BANK_STATEMENT_ANALYSER_STATUS, PERFIOS_BANK_STATEMENT_ANALYSER_STATUS } from "constants/enums";

// Column header + hover explainer, used by Relevance / 30D / 6M — all three
// are easy to misread without the underlying definition spelled out.
const renderColumnTitleWithInfo = (label, tooltip) => (
    <span>
        {label}
        <Tooltip title={tooltip}>
            <Icon type="info-circle" style={{ marginLeft: 5, color: "#8a93a3" }} />
        </Tooltip>
    </span>
);

// How relevant the uploaded statement is to the assessment. Colour + icon make
// the three bands scannable at a glance: Low = red, Medium = amber, High = green.
const STATEMENT_RELEVANCE = {
    low: { label: "Low", color: "#f5222d", icon: "close-circle" },
    medium: { label: "Medium", color: "#faad14", icon: "exclamation-circle" },
    high: { label: "High", color: "#52c41a", icon: "check-circle" }
};

const renderStatementRelevance = (_) => {
    if (_ === null || _ === undefined || _ === "") {
        return "--";
    }
    const band = STATEMENT_RELEVANCE[String(_).trim().toLowerCase()];
    // Unknown tag: show the raw value rather than hiding it.
    if (!band) {
        return String(_);
    }
    return (
        <span
            style={{
                color: band.color,
                display: "inline-flex",
                alignItems: "center"
            }}
        >
            <Icon type={band.icon} style={{ marginRight: "6px" }} />
            {band.label}
        </span>
    );
};

// Plain boolean flags rendered as an icon only — tick for true, cross for false.
const renderBooleanIcon = (_) => {
    if (_ === null || _ === undefined || _ === "") {
        return "--";
    }
    return _ ? (
        <Icon type="check-circle" style={{ color: "#52c41a" }} />
    ) : (
        <Icon type="close-circle" style={{ color: "#f5222d" }} />
    );
};

const renderJobStatus = (_, props) => {
    const statusMap = props.provider
        ? PERFIOS_BANK_STATEMENT_ANALYSER_STATUS
        : BANK_STATEMENT_ANALYSER_STATUS;
    const label = statusMap.findKey((status) => status == _);
    const color = _ === 4 ? "red" : _ === 5 ? "green" : null;
    return <span style={{ color }}>{label}</span>;
};

export const JOB_LIST_COLUMNS = new OrderedMap({
    select: {
        key: "select",
        dataIndex: "select",
        title: "",
        align: "center",
        width: 50,
    },
    analysis_request: {
        key: "analysis_request",
        dataIndex: "analysis_request",
        title: "Request Id",
        render: (_) => _,
        align: "center",
        // Narrowed to make room for the 30D/6M columns — the id is a short
        // number, it doesn't need as much space as the other columns.
        width: 90,
    },
    bank: {
        key: "bank",
        dataIndex: "bank",
        title: "Bank",
        align: "center",
    },
    account_name: {
        key: "account_name",
        dataIndex: "account_name",
        title: "Name",
        align: "left",
    },
    account_type: {
        key: "account_type",
        dataIndex: "account_type",
        title: "Account Type",
        align: "center",
    },
    create_date: {
        key: "create_date",
        dataIndex: "create_date",
        title: "Create Date",
        render: (_) => _ && _.format("DD MMM YYYY"),
        align: "center",
    },
    credit_limit: {
        key: "credit_limit",
        dataIndex: "credit_limit",
        title: "Credit Limit",
        align: "right",
    },
    statement_relevance_tag: {
        key: "statement_relevance_tag",
        dataIndex: "statement_relevance_tag",
        title: renderColumnTitleWithInfo(
            "Relevance",
            "Whether the uploaded statement belongs to the account reported to us."
        ),
        // Plain-text fallback for the Excel export header (see table.js's
        // onDownloadXlsx) since `title` above is a ReactNode, not a string.
        exportTitle: "Relevance",
        render: renderStatementRelevance,
        align: "center",
    },
    last_30_day: {
        key: "last_30_day",
        dataIndex: "last_30_day",
        title: renderColumnTitleWithInfo(
            "30D",
            "Whether the statement file covers a period within the last 30 days."
        ),
        exportTitle: "30D",
        render: renderBooleanIcon,
        align: "center",
        width: 80,
    },
    atleast_6m: {
        key: "atleast_6m",
        dataIndex: "atleast_6m",
        title: renderColumnTitleWithInfo(
            "6M",
            "Whether the statement has at least 6 months of data."
        ),
        exportTitle: "6M",
        render: renderBooleanIcon,
        align: "center",
        width: 60,
    },
    fraud: {
        key: "fraud",
        dataIndex: "fraud",
        title: "Fraud Count",
        align: "center",
        width: 110,
    },
    provider: {
        key: "provider_name",
        dataIndex: "provider_name",
        title: "Provider",
        align: "center",
    },
    current_status: {
        key: "current_status",
        dataIndex: "current_status",
        title: "Current Status",
        render: renderJobStatus,
        align: "center",
    },
    download: {
        key: "download",
        dataIndex: "download",
        title: "Download Bank statement",
        align: "center",
    },
    using: {
        key: "using",
        dataIndex: "using",
        title: "Using",
        render: (_) => (_ ? "Yes" : "No"),
        align: "center",
    },
    error: {
        key: "error",
        dataIndex: "error",
        title: "Bank Statement Analysis",
        align: "center",
    },
    redo: {
        key: "redo",
        dataIndex: "redo",
        title: "Redo",
        align: "center",
        width: 90,
    },
});

export const USFB_ANALYSIS_TABLE = new OrderedMap({
    month: {
        title: "Months",
        key: "month",
        dataIndex: "month",
        render: (_) => (_ ? moment(_, "YYYYMM").format("MM-YYYY") : "--"),
    },
    monthlyAverageBalanceOn10th: {
        title: "10th Balance",
        key: "monthlyAverageBalanceOn10th",
        dataIndex: "monthlyAverageBalanceOn10th",
        render: (_) => _ && formatCurrency(_),
    },
    monthlyAverageBalanceOn15th: {
        title: "15th Balance",
        key: "monthlyAverageBalanceOn15th",
        dataIndex: "monthlyAverageBalanceOn15th",
        render: (_) => _ && formatCurrency(_),
    },
    monthlyAverageBalanceOn20th: {
        title: "20th Balance",
        key: "monthlyAverageBalanceOn20th",
        dataIndex: "monthlyAverageBalanceOn20th",
        render: (_) => _ && formatCurrency(_),
    },
    monthlyAverageBalanceOn25th: {
        title: "25th Balance",
        key: "monthlyAverageBalanceOn25th",
        dataIndex: "monthlyAverageBalanceOn25th",
        render: (_) => _ && formatCurrency(_),
    },
    monthlyContraDebit: {
        title: "Inward Cheques Bounce",
        key: "monthlyContraDebit",
        dataIndex: "monthlyContraDebit",
        render: (_) => (_ ? (typeof _ != "object" ? 1 : (_.length || "--")) : 0),
    },
    monthlyContraCredit: {
        title: "Outward Cheques Bounce",
        key: "monthlyContraCredit",
        dataIndex: "monthlyContraCredit",
        render: (_) => (_ ? (typeof _ != "object" ? 1 : (_.length || "--")) : 0),
    },
    monthlyTotalCredits: {
        title: "Monthly Credits",
        key: "monthlyTotalCredits",
        dataIndex: "monthlyTotalCredits",
        render: (_) => _ && formatCurrency(_),
    },
    monthlyTotalDebits: {
        title: "Monthly Debits",
        key: "monthlyTotalDebits",
        dataIndex: "monthlyTotalDebits",
        render: (_) => _ && formatCurrency(_),
    },
});
